import { useUploadDashboardBackgroundImage } from "@/api/dashboard-home";
import {
  type BackgroundImageId,
  type DashboardBackground,
  DEFAULT_SOLID_COLOR,
  GRADIENT_PRESETS,
  imageUrlFor,
  isAcceptedBackgroundImage,
  PRESET_IMAGE_IDS,
  type UserImageSlot,
} from "@/lib/dashboard-home/background";
import { contrastForImage } from "@/lib/dashboard-home/background-contrast";
import type { BackgroundContrast } from "@/lib/dashboard-home/contrast";
import { useEffect, useRef, useState } from "react";

/**
 * Brouillon de la modale « Configurer la page » : chaque option garde sa
 * dernière valeur (on peut passer de la couleur au dégradé et revenir), et
 * l'aperçu en direct est poussé à la page à chaque changement.
 */
export type BackgroundDraft = {
  type: DashboardBackground["type"];
  color: string;
  gradientId: string;
  /** Image retenue parmi les six (la dernière choisie si le type n'est pas « image »). */
  imageId: BackgroundImageId;
  /** URL enregistrées côté API des trois images personnelles, ou `blob:` d'un fichier choisi. */
  userImageUrls: (string | null)[];
  /** Fichiers choisis mais pas encore envoyés, par emplacement. */
  pendingFiles: Partial<Record<UserImageSlot, File>>;
  /** Mesures de contraste déjà faites, par image. */
  imageContrasts: Partial<Record<BackgroundImageId, BackgroundContrast>>;
};

export type BackgroundPreview = {
  background: DashboardBackground;
  imageUrl: string | null;
};

export type SaveResult =
  | { outcome: "saved"; background: DashboardBackground }
  /** « Image » choisi sans image exploitable : il faut en choisir une. */
  | { outcome: "needs-image" }
  /** L'envoi d'une image a échoué. */
  | { outcome: "failed" };

function draftFrom(
  background: DashboardBackground,
  userImageUrls: (string | null)[],
): BackgroundDraft {
  const isImage = background.type === "image";
  return {
    type: background.type,
    color: background.type === "color" ? background.color : DEFAULT_SOLID_COLOR,
    gradientId:
      background.type === "gradient"
        ? background.gradientId
        : GRADIENT_PRESETS[0].id,
    imageId: isImage ? background.imageId : PRESET_IMAGE_IDS[0],
    userImageUrls,
    pendingFiles: {},
    imageContrasts: isImage
      ? { [background.imageId]: background.contrast }
      : {},
  };
}

/** URL de l'image retenue dans le brouillon. */
function imageUrlOf(draft: BackgroundDraft): string | null {
  return imageUrlFor(draft.imageId, draft.userImageUrls);
}

/** Fond correspondant au brouillon, ou `null` si « image » est choisi sans image mesurée. */
function backgroundFrom(draft: BackgroundDraft): DashboardBackground | null {
  switch (draft.type) {
    case "color":
      return { type: "color", color: draft.color };
    case "gradient":
      return { type: "gradient", gradientId: draft.gradientId };
    case "image": {
      const contrast = draft.imageContrasts[draft.imageId];
      return imageUrlOf(draft) && contrast
        ? { type: "image", imageId: draft.imageId, contrast }
        : null;
    }
    default:
      return { type: "none" };
  }
}

export function useBackgroundDraft({
  background,
  userImageUrls,
  onPreview,
}: {
  background: DashboardBackground;
  userImageUrls: (string | null)[];
  onPreview: (preview: BackgroundPreview | null) => void;
}) {
  const [draft, setDraft] = useState(() =>
    draftFrom(background, userImageUrls),
  );
  // Brouillon courant, lisible par plusieurs mises à jour du même événement
  // (pastille du popover + ligne d'option) et après une analyse asynchrone.
  const draftRef = useRef(draft);
  const [analyzing, setAnalyzing] = useState(false);
  const upload = useUploadDashboardBackgroundImage();

  // Une analyse peut se terminer après la fermeture de la modale.
  const mountedRef = useRef(true);
  useEffect(() => {
    // Remis à `true` à chaque montage : StrictMode (dev) monte, démonte puis
    // remonte, et laisserait sinon `false` pour toujours — toute analyse
    // d'image serait ignorée, donc aucune image ne se sélectionnerait.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Les URL `blob:` des fichiers choisis mais pas envoyés ne servent plus.
      const { pendingFiles, userImageUrls: urls } = draftRef.current;
      for (const slot of Object.keys(pendingFiles)) {
        const url = urls[Number(slot) - 1];
        if (url) URL.revokeObjectURL(url);
      }
    };
  }, []);

  const update = (patch: Partial<BackgroundDraft>) => {
    const next = { ...draftRef.current, ...patch };
    draftRef.current = next;
    setDraft(next);
    const preview = backgroundFrom(next);
    if (preview) onPreview({ background: preview, imageUrl: imageUrlOf(next) });
  };

  const analyze = async (url: string) => {
    setAnalyzing(true);
    const contrast = await contrastForImage(url);
    if (mountedRef.current) setAnalyzing(false);
    return mountedRef.current ? contrast : null;
  };

  /** `false` si le fichier est refusé (format ou poids). */
  const chooseFile = async (
    slot: UserImageSlot,
    file: File,
  ): Promise<boolean> => {
    if (!isAcceptedBackgroundImage(file)) return false;
    const url = URL.createObjectURL(file);
    // Mesure sur le fichier local : l'URL S3 signée ne serait pas lisible (CORS).
    const contrast = await analyze(url);
    if (!contrast) {
      URL.revokeObjectURL(url);
      return true;
    }
    const current = draftRef.current;
    // Un fichier déjà choisi pour cet emplacement est remplacé : on libère son URL.
    if (current.pendingFiles[slot]) {
      const previous = current.userImageUrls[slot - 1];
      if (previous) URL.revokeObjectURL(previous);
    }
    const urls = [...current.userImageUrls];
    urls[slot - 1] = url;
    const imageId = `user-${slot}` as const;
    update({
      type: "image",
      imageId,
      userImageUrls: urls,
      pendingFiles: { ...current.pendingFiles, [slot]: file },
      imageContrasts: { ...current.imageContrasts, [imageId]: contrast },
    });
    return true;
  };

  /** Sélectionne une image ; `false` si c'est un emplacement vide (il faut choisir un fichier). */
  const selectImage = async (imageId: BackgroundImageId): Promise<boolean> => {
    const url = imageUrlFor(imageId, draftRef.current.userImageUrls);
    if (!url) return false;
    const known = draftRef.current.imageContrasts[imageId];
    if (known) {
      update({ type: "image", imageId });
      return true;
    }
    // Image jamais mesurée (fournie, ou enregistrée sans mesure) : repli sûr si CORS.
    const contrast = await analyze(url);
    if (contrast) {
      update({
        type: "image",
        imageId,
        imageContrasts: {
          ...draftRef.current.imageContrasts,
          [imageId]: contrast,
        },
      });
    }
    return true;
  };

  /** Envoie les fichiers choisis ; renvoie le fond à enregistrer. */
  const save = async (): Promise<SaveResult> => {
    const current = draftRef.current;
    const next = backgroundFrom(current);
    if (!next) return { outcome: "needs-image" };
    if (current.type === "image") {
      try {
        for (const [slot, file] of Object.entries(current.pendingFiles)) {
          await upload.mutateAsync({ slot: Number(slot), file });
        }
      } catch {
        return { outcome: "failed" };
      }
    }
    return { outcome: "saved", background: next };
  };

  return {
    draft,
    update,
    analyzing,
    saving: upload.isPending,
    chooseFile,
    selectImage,
    save,
  };
}
